import {
	Alert,
	Button,
	ButtonDropdown,
	Detail,
	DropdownList,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	IconTooltip,
	Label,
	Modal,
	Text,
	TextField,
} from '@forecast-it/design-system';
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {MODAL_TYPE, showModal} from '../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../shared/components/modals/types';
import {useForecastFetchQuery} from '../../../shared/hooks/useForecastFetchQuery';
import Util from '../../../shared/util/util';
import {profileLabel, StageProfile} from '../../../shared/util/projectStageUtil';
import CreateProjectStageMutation from '../../../../mutations/CreateProjectStageMutation';
import UpdateProjectStageMutation from '../../../../mutations/UpdateProjectStageMutation';
import {
	isStageLastWithProfile,
	MAX_STAGE_NAME_LENGTH,
	StageNameError,
	stageNameErrorFromServer,
	validateStageName,
} from '../projectStageValidation';
import {RemapImpactSection, remapImpactSections} from '../remapImpactSections';
import type {
	ProjectStageModalRemapImpactQuery$data,
	ProjectStageModalRemapImpactQuery$variables,
} from './__generated__/ProjectStageModalRemapImpactQuery.graphql';

export type ProjectStageModalPayload = {
	/** The company's Relay id; a new stage is appended to its projectStages connection. */
	companyId: string;
	/** The stage being edited. Absent when adding a stage. */
	stage?: {id: string; name: string; profile: StageProfile};
	/** Every other stage's stored name, defaults included, for the duplicate check. */
	otherNames: ReadonlyArray<string>;
	/** Whether no other stage has the edited stage's profile, as the page saw it. */
	lastWithProfile?: boolean;
};

export const showProjectStageModal = (props: ProjectStageModalPayload): void => {
	showModal({
		type: MODAL_TYPE.PROJECT_STAGE,
		...props,
	});
};

const ERROR_MESSAGE_IDS: Record<StageNameError, string> = {
	required: 'settings.project_stages.error.name_required',
	duplicate: 'settings.project_stages.error.name_duplicate',
};

const PROFILES: StageProfile[] = ['OPPORTUNITY', 'PLANNING', 'RUNNING', 'HALTED', 'DONE'];

const RemapImpactQuery = graphql`
	query ProjectStageModalRemapImpactQuery($stageId: ID!, $profile: PROJECT_STATUS!) {
		viewer {
			company {
				projectStageRemapImpact(stageId: $stageId, profile: $profile) {
					sage {
						activated
						created
						deactivated
						timeBlocked
					}
					jira {
						stopSyncing
						startSyncing
					}
					sageSyncOnSave
				}
			}
		}
	}
`;

/** Adds a project stage, or edits one's name and what it behaves like. Saving happens here, not on the page. */
export const ProjectStageModal = ({
	closeModal,
	companyId,
	stage,
	otherNames,
	lastWithProfile: lastWithProfileOnOpen = false,
}: ModalProps<ProjectStageModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [name, setName] = useState(stage?.name ?? '');
	const [profile, setProfile] = useState<StageProfile>(stage?.profile ?? 'PLANNING');
	const [touched, setTouched] = useState(false);
	const [saving, setSaving] = useState(false);
	const [serverError, setServerError] = useState<StageNameError | null>(null);
	const [lastWithProfile, setLastWithProfile] = useState(lastWithProfileOnOpen);
	const [warning, setWarning] = useState(false);
	const [checking, setChecking] = useState(false);
	const [impactSections, setImpactSections] = useState<RemapImpactSection[]>([]);
	const [sageSyncOnSave, setSageSyncOnSave] = useState(false);
	const {fetch: fetchRemapImpact} = useForecastFetchQuery<
		ProjectStageModalRemapImpactQuery$data,
		ProjectStageModalRemapImpactQuery$variables
	>(RemapImpactQuery);
	// A check answers for the profile it was asked about; one that lands after the admin picked another is dropped.
	const currentProfile = useRef(profile);
	currentProfile.current = profile;

	// A save outlives the modal if it is cancelled or dismissed mid-flight. The conductor's closeModal
	// pops whichever modal is on top, so a late success must not call it for a modal that is already gone.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Opportunity is offered with opportunity access, or when it is already this stage's profile.
	const profileOptions = PROFILES.filter(
		value => value !== 'OPPORTUNITY' || Util.hasOpportunityAccess() || stage?.profile === 'OPPORTUNITY'
	).map(value => ({id: value, name: profileLabel(value, intl)}));

	const remapping = !!stage && profile !== stage.profile;
	const localError = validateStageName(name, otherNames);
	// "Required" is only worth saying once the admin has typed and cleared the field; an untouched empty
	// field just keeps Save disabled.
	const shownError = serverError ?? (localError === 'required' && !touched ? null : localError);

	const handleChange = (value: string) => {
		setName(value);
		setTouched(true);
		setServerError(null);
	};

	const handleProfileChange = (value: StageProfile) => {
		setProfile(value);
		setWarning(false);
		setImpactSections([]);
		setSageSyncOnSave(false);
	};

	// Saving only ends on a path that really ends it: the default handler returns true when it has
	// scheduled a retry, and Save must stay disabled until that attempt settles.
	const handleError = (error: Error, onFailure: (error: Error) => boolean | void) => {
		if (!mounted.current) {
			onFailure(error);
			return;
		}
		const nameError = stageNameErrorFromServer(error);
		if (nameError) {
			setServerError(nameError);
			setSaving(false);
			return;
		}
		if (isStageLastWithProfile(error)) {
			// Another stage with the old profile went away since the page loaded: warn, then let the admin go ahead.
			setLastWithProfile(true);
			setWarning(true);
			setSaving(false);
			return;
		}
		if (onFailure(error) !== true) {
			setSaving(false);
		}
	};

	const handleSaved = () => {
		if (mounted.current) {
			closeModal();
		}
	};

	const commit = (allowLastWithProfile: boolean) => {
		setSaving(true);
		const trimmed = name.trim();
		if (stage) {
			Util.CommitMutation(
				UpdateProjectStageMutation,
				{id: stage.id, name: trimmed, profile, allowLastWithProfile},
				handleSaved,
				true,
				null,
				handleError
			);
		} else {
			// Creating is not idempotent: if the first request committed but its response was lost, an
			// automatic retry would be refused as a duplicate.
			Util.CommitMutation(
				CreateProjectStageMutation,
				{companyId, name: trimmed, profile},
				handleSaved,
				false,
				null,
				handleError
			);
		}
	};

	// Asks what the remap changes in the company's integrations, and warns when anything does. The check only reads
	// Forecast's own data, so it rarely fails; when it does, the save carries on as if nothing changes, because the
	// warning must never block a remap. The name and profile are locked while it runs, because its answer saves what
	// was on screen when Save was pressed.
	const checkRemapImpact = (stageId: string) => {
		const checkedProfile = profile;
		const settle = (sections: RemapImpactSection[], savingCallsSage: boolean) => {
			if (!mounted.current) {
				return;
			}
			setChecking(false);
			if (currentProfile.current !== checkedProfile) {
				return;
			}
			setImpactSections(sections);
			setSageSyncOnSave(savingCallsSage);
			if (sections.length > 0 || lastWithProfile) {
				setWarning(true);
			} else {
				commit(false);
			}
		};
		setChecking(true);
		fetchRemapImpact(
			{stageId, profile: checkedProfile},
			data => {
				const impact = data.viewer?.company?.projectStageRemapImpact;
				settle(remapImpactSections(impact), impact?.sageSyncOnSave === true);
			},
			() => settle([], false)
		);
	};

	const handleSave = () => {
		if (localError || saving || checking) {
			return;
		}
		if (remapping && stage && !warning) {
			checkRemapImpact(stage.id);
			return;
		}
		// The go-ahead is only for the last-stage refusal, and only once the admin has seen that warning.
		commit(warning && lastWithProfile);
	};

	const title = intl.formatMessage({id: stage ? 'settings.project_stages.edit_title' : 'settings.project_stages.add_title'});
	const saveLabel = warning
		? intl.formatMessage({id: 'settings.project_stages.change_anyway'})
		: intl.formatMessage({id: stage ? 'settings.project_stages.save_stage' : 'settings.project_stages.add'});
	// A remap whose save calls Sage waits for those calls before it answers, which can take seconds, even when nothing
	// changes state in Sage; every other save is quick enough for a disabled button.
	const savingThroughSage = saving && sageSyncOnSave;

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{title}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap={'l'}>
					<TextField
						label={intl.formatMessage({id: 'settings.project_stages.name_label'})}
						value={name}
						onValueChange={handleChange}
						maxLength={MAX_STAGE_NAME_LENGTH}
						disabled={checking}
						errorText={shownError ? intl.formatMessage({id: ERROR_MESSAGE_IDS[shownError]}) : ''}
						data-cy="project-stage-name-field"
					/>
					<FlexColumn gap={'xs'}>
						<FlexRow gap={'xs'} alignItems={'center'}>
							<Label>{intl.formatMessage({id: 'settings.project_stages.behaves_like'})}</Label>
							<IconTooltip icon={'info'}>
								<IconTooltip.Item
									title={intl.formatMessage({id: 'settings.project_stages.behaves_like'})}
									details={intl.formatMessage({id: 'settings.project_stages.behaves_like.tooltip'})}
								/>
							</IconTooltip>
						</FlexRow>
						<div>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdown
										emphasis={EMPHASIS.LOW}
										aria-label={`${intl.formatMessage({
											id: 'settings.project_stages.behaves_like',
										})} ${profileLabel(profile, intl)}`}
										data-cy="project-stage-profile"
										disabled={checking}
									>
										{profileLabel(profile, intl)}
									</ButtonDropdown>
								</DropdownList.Trigger>
								<DropdownList.Content offset={4} align={'start'}>
									{profileOptions.map(option => (
										<DropdownList.Item
											key={option.id}
											onClick={() => handleProfileChange(option.id)}
											data-cy={`project-stage-profile-${option.id}`}
										>
											{option.name}
										</DropdownList.Item>
									))}
								</DropdownList.Content>
							</DropdownList>
						</div>
						<Text data-cy="project-stage-profile-description">
							{intl.formatMessage({id: `settings.project_stages.profile_description.${profile.toLowerCase()}`})}
						</Text>
					</FlexColumn>
					{warning && stage ? (
						<div role="alert" data-cy="project-stage-remap-warning">
							<FlexColumn gap={'m'}>
								{lastWithProfile ? (
									<Text>
										{intl.formatMessage(
											{id: 'settings.project_stages.remap_last_with_profile'},
											{name: stage.name, profile: profileLabel(stage.profile, intl)}
										)}
									</Text>
								) : null}
								{impactSections.length > 0 ? (
									<div data-cy="project-stage-remap-heading">
										<Alert
											type={Alert.TYPE.WARNING}
											text={intl.formatMessage(
												{id: 'settings.project_stages.remap_impact.heading'},
												{name: stage.name}
											)}
										/>
									</div>
								) : null}
								{impactSections.map(section => (
									<div key={section.id} data-cy="project-stage-remap-section">
										<FlexColumn gap={'xs'}>
											<Label data-cy="project-stage-remap-section-label">
												{intl.formatMessage({id: section.id})}
											</Label>
											<ul>
												{section.lines.map(line => (
													<li key={line.id}>
														<Text>{intl.formatMessage({id: line.id}, {count: line.count})}</Text>
														{line.detailId ? (
															<Detail color={'medium'}>
																{intl.formatMessage({id: line.detailId})}
															</Detail>
														) : null}
													</li>
												))}
											</ul>
										</FlexColumn>
									</div>
								))}
							</FlexColumn>
						</div>
					) : null}
				</FlexColumn>
				<FlexRow>
					{/* The footer button renders this same Button but does not pass `waiting` through its type. */}
					<Button
						onPress={handleSave}
						disabled={!!localError || saving || checking}
						waiting={savingThroughSage}
						data-cy="project-stage-save"
					>
						{saveLabel}
					</Button>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						{intl.formatMessage({id: 'common.cancel'})}
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
